import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import {
  "ui-controls.decrease" as decreaseLabel,
  "ui-controls.increase" as increaseLabel,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { Minus, Plus } from "lucide-react";
import { useCallback } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "./icon";

/**
 * −, a number, + — for small bounded counts: days a week, hours of sleep.
 * A2's `Stepper`, on Base UI's NumberField.
 *
 * ── TAP TO STEP, KEYS TO STEP, NO TYPING ────────────────────────────────────
 * The number is NumberField's input, `readOnly`: ↑/↓, Home and End step and
 * clamp through Base UI, and a tap never opens the soft keyboard over a value
 * that two buttons already reach. A value someone types (a weight, a height)
 * is `NumberField`, not this.
 *
 * ── CLAMPED BOTH WAYS ───────────────────────────────────────────────────────
 * Base UI clamps every step to `min`/`max` and disables the button at a
 * bound, so − at `min` does nothing and says so.
 *
 * ── ANNOUNCED ───────────────────────────────────────────────────────────────
 * Base UI's buttons are out of the tab order (the input takes the keys), and
 * a screen reader's touch user presses them without focus moving to the
 * value. A polite live region repeats the formatted value, so each press is
 * heard.
 */

export interface StepperProps {
  className?: string;
  disabled?: boolean;
  /** How the value reads, e.g. `{ style: "unit", unit: "hour" }` → "7,5 h". */
  format?: Intl.NumberFormatOptions;
  /** What is being counted; it names the input and both buttons. */
  label: string;
  /** Defaults to the app's current locale. */
  locale?: string;
  max: number;
  min: number;
  onValueChange: (value: number) => void;
  step?: number;
  value: number;
}

const STEP_BUTTON =
  "a-press grid size-12 shrink-0 cursor-pointer place-items-center rounded-full bg-surface-2 text-ink data-disabled:cursor-not-allowed data-disabled:opacity-40";

export function Stepper({
  value,
  onValueChange,
  min,
  max,
  step,
  label,
  format,
  locale,
  disabled,
  className,
}: StepperProps) {
  const lang = locale ?? getLocale();
  // A step never produces `null` (only clearing the input does, and the input
  // is read-only), so a null is ignored rather than passed on.
  const change = useCallback(
    (next: number | null) => {
      if (next !== null) {
        onValueChange(next);
      }
    },
    [onValueChange]
  );
  return (
    <BaseNumberField.Root
      className={cx("flex items-center gap-2", className)}
      disabled={disabled}
      format={format}
      locale={lang}
      max={max}
      min={min}
      onValueChange={change}
      step={step ?? 1}
      value={value}
    >
      <BaseNumberField.Decrement
        aria-label={decreaseLabel({ label })}
        className={STEP_BUTTON}
      >
        <Icon bold icon={Minus} />
      </BaseNumberField.Decrement>
      <BaseNumberField.Input
        aria-label={label}
        className="a-display w-16 min-w-11 text-center text-ink text-xl tabular-nums"
        readOnly
      />
      <BaseNumberField.Increment
        aria-label={increaseLabel({ label })}
        className={STEP_BUTTON}
      >
        <Icon bold icon={Plus} />
      </BaseNumberField.Increment>
      <span aria-live="polite" className="sr-only">
        {new Intl.NumberFormat(lang, format).format(value)}
      </span>
    </BaseNumberField.Root>
  );
}
